import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { sendSignedDocumentEmail } from "../../../api/hrm/signing/signingRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";

const SigningSendEmailModal = ({ open, requestId, requestTitle, requestCode, onClose, onSent }) => {
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToEmail("");
    setSubject(requestTitle ? `[IMA] Văn bản đã ký: ${requestTitle}` : "");
    setMessage("");
  }, [open, requestTitle]);

  const handleSend = async () => {
    const trimmedEmail = toEmail.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail) {
      showErrorToast("Nhập email người nhận.");
      return;
    }
    if (!trimmedMessage) {
      showErrorToast("Nhập nội dung email.");
      return;
    }
    setSending(true);
    try {
      const res = await sendSignedDocumentEmail(requestId, {
        to_email: trimmedEmail,
        subject: subject.trim() || undefined,
        message: trimmedMessage,
      });
      showSuccessToast(res?.message || "Đã gửi email.");
      onSent?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi gửi email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="font-black uppercase tracking-tight text-gray-800 flex items-center gap-2">
                  <Mail size={18} className="text-red-700" />
                  Gửi email
                </h3>
                <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100">
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  File đính kèm: {requestCode || "—"}-signed.pdf
                </p>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                    Email người nhận *
                  </label>
                  <input
                    type="text"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder="email@example.com (nhiều email cách nhau bằng dấu phẩy)"
                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                    Tiêu đề
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Tiêu đề email"
                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                    Nội dung *
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    placeholder="Nhập nội dung email gửi cho người nhận..."
                    className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 resize-y min-h-[120px]"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex gap-3 justify-end bg-gray-50/50">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-full border border-gray-200 text-gray-600"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full text-xs font-bold uppercase tracking-wider disabled:opacity-50 hover:shadow-lg hover:shadow-red-100 transition-all"
                >
                  {sending ? "Đang gửi..." : "Gửi email"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SigningSendEmailModal;
